import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, MapPin, Users, Check, X, Plus } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    type: "",
    maxSpots: "",
    availableSpots: ""
  });
  const { toast } = useToast();

  useEffect(() => {
    loadEvents();
    loadBookings();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events`);
      const data = await response.json();
      setEvents(data);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings`);
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const handleEventFormChange = (e) => {
    setEventForm({
      ...eventForm,
      [e.target.name]: e.target.value
    });
  };

  const handleEventTypeChange = (value) => {
    setEventForm({
      ...eventForm,
      type: value
    });
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const eventData = {
        ...eventForm,
        date: new Date(eventForm.date).toISOString(),
        maxSpots: parseInt(eventForm.maxSpots),
        availableSpots: parseInt(eventForm.availableSpots)
      };

      const response = await fetch(`${BACKEND_URL}/api/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });

      if (response.ok) {
        toast({
          title: "Event Created!",
          description: "New event has been added to the calendar.",
        });
        
        setEventForm({
          title: "",
          description: "",
          date: "",
          time: "",
          location: "",
          type: "",
          maxSpots: "",
          availableSpots: ""
        });
        setShowEventForm(false);
        loadEvents();
      } else {
        throw new Error('Failed to create event');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create event. Please try again.",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const handleBookingStatusUpdate = async (bookingId, status) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        toast({
          title: status === 'approved' ? "Booking Approved!" : "Booking Rejected",
          description: `The booking has been ${status}.`,
        });
        loadBookings();
        loadEvents(); // Reload events to update available spots
      } else {
        throw new Error('Failed to update booking');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update booking status.",
        variant: "destructive"
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-600';
      case 'rejected': return 'bg-red-600';
      default: return 'bg-yellow-600';
    }
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'class': return 'bg-red-600';
      case 'sparring': return 'bg-orange-600';
      case 'personal': return 'bg-red-700';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-red-600 mb-2">Admin Dashboard</h1>
          <p className="text-gray-300">Manage events and booking requests</p>
        </div>

        {/* Events Management */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <Card className="bg-gray-900 border-red-600/20">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Events Management</CardTitle>
              <Button
                onClick={() => setShowEventForm(!showEventForm)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Plus size={16} className="mr-2" />
                Add Event
              </Button>
            </CardHeader>
            <CardContent>
              {showEventForm && (
                <form onSubmit={handleCreateEvent} className="space-y-4 mb-6 p-4 border border-gray-700 rounded-lg">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      name="title"
                      placeholder="Event Title *"
                      value={eventForm.title}
                      onChange={handleEventFormChange}
                      required
                      className="bg-black border-gray-700 text-white"
                    />
                    <Input
                      name="location"
                      placeholder="Location *"
                      value={eventForm.location}
                      onChange={handleEventFormChange}
                      required
                      className="bg-black border-gray-700 text-white"
                    />
                  </div>
                  
                  <Textarea
                    name="description"
                    placeholder="Event Description *"
                    value={eventForm.description}
                    onChange={handleEventFormChange}
                    required
                    className="bg-black border-gray-700 text-white"
                  />
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <Input
                      name="date"
                      type="datetime-local"
                      value={eventForm.date}
                      onChange={handleEventFormChange}
                      required
                      className="bg-black border-gray-700 text-white"
                    />
                    <Input
                      name="time"
                      placeholder="Time Range *"
                      value={eventForm.time}
                      onChange={handleEventFormChange}
                      required
                      className="bg-black border-gray-700 text-white"
                    />
                    <Select value={eventForm.type} onValueChange={handleEventTypeChange}>
                      <SelectTrigger className="bg-black border-gray-700 text-white">
                        <SelectValue placeholder="Event Type *" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="class" className="text-white">Class</SelectItem>
                        <SelectItem value="sparring" className="text-white">Sparring</SelectItem>
                        <SelectItem value="personal" className="text-white">Personal</SelectItem>
                        <SelectItem value="competition" className="text-white">Competition</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      name="maxSpots"
                      type="number"
                      placeholder="Max Spots *"
                      value={eventForm.maxSpots}
                      onChange={handleEventFormChange}
                      required
                      className="bg-black border-gray-700 text-white"
                    />
                    <Input
                      name="availableSpots"
                      type="number"
                      placeholder="Available Spots *"
                      value={eventForm.availableSpots}
                      onChange={handleEventFormChange}
                      required
                      className="bg-black border-gray-700 text-white"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700">
                      {loading ? 'Creating...' : 'Create Event'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowEventForm(false)}
                      variant="outline"
                      className="border-gray-700 text-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {events.map((event) => (
                  <div key={event.id} className="bg-black p-4 rounded-lg border border-gray-700">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold">{event.title}</h4>
                      <Badge className={`${getEventTypeColor(event.type)} text-white`}>
                        {event.type}
                      </Badge>
                    </div>
                    <div className="text-gray-300 text-sm space-y-1">
                      <div className="flex items-center">
                        <Calendar className="mr-2" size={14} />
                        <span>{new Date(event.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="mr-2" size={14} />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="mr-2" size={14} />
                        <span>{event.location}</span>
                      </div>
                      <div className="flex items-center">
                        <Users className="mr-2" size={14} />
                        <span>{event.availableSpots}/{event.maxSpots} spots available</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Booking Requests */}
          <Card className="bg-gray-900 border-red-600/20">
            <CardHeader>
              <CardTitle className="text-white">Booking Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {bookings.filter(booking => booking.status === 'pending').map((booking) => {
                  const event = events.find(e => e.id === booking.eventId);
                  return (
                    <div key={booking.id} className="bg-black p-4 rounded-lg border border-gray-700">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-white font-semibold">{booking.name}</h4>
                          <p className="text-gray-400 text-sm">{booking.email}</p>
                          {booking.phone && (
                            <p className="text-gray-400 text-sm">{booking.phone}</p>
                          )}
                        </div>
                        <Badge className={`${getStatusColor(booking.status)} text-white`}>
                          {booking.status}
                        </Badge>
                      </div>
                      
                      {event && (
                        <div className="text-gray-300 text-sm mb-3">
                          <p className="font-semibold">{event.title}</p>
                          <p>{new Date(event.date).toLocaleDateString()} - {event.time}</p>
                        </div>
                      )}
                      
                      {booking.message && (
                        <p className="text-gray-300 text-sm mb-3 italic">"{booking.message}"</p>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleBookingStatusUpdate(booking.id, 'approved')}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1"
                          size="sm"
                        >
                          <Check size={14} className="mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleBookingStatusUpdate(booking.id, 'rejected')}
                          className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1"
                          size="sm"
                        >
                          <X size={14} className="mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  );
                })}
                
                {bookings.filter(booking => booking.status === 'pending').length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No pending booking requests</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* All Bookings History */}
        <Card className="bg-gray-900 border-red-600/20">
          <CardHeader>
            <CardTitle className="text-white">All Bookings History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {bookings.map((booking) => {
                const event = events.find(e => e.id === booking.eventId);
                return (
                  <div key={booking.id} className="bg-black p-3 rounded-lg border border-gray-700 flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">{booking.name}</p>
                      <p className="text-gray-400 text-sm">{event?.title || 'Unknown Event'}</p>
                      <p className="text-gray-500 text-xs">{new Date(booking.createdAt).toLocaleDateString()}</p>
                    </div>
                    <Badge className={`${getStatusColor(booking.status)} text-white`}>
                      {booking.status}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPage;